import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useAppMode } from '../../contexts/AppModeContext'
import { useAudioAlerts } from '../../hooks/useAudioAlerts'
import { AgentAvailabilityToggle } from '../chat/AgentAvailabilityToggle'
import { CrmMetricsGlossary } from '../crm/CrmMetricsGlossary'
import { apiGet } from '../../utils/api'
import { CrmAlertsBell } from '../crm/CrmAlerts'

/* ─── icons ─────────────────────────────────────────────────────────────── */
const SparklesIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
  </svg>
)
const SpeakerOnIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
  </svg>
)
const SpeakerOffIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75L19.5 12m0 0l2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6l4.72-4.72a.75.75 0 011.28.531V19.94a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.506-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
  </svg>
)
const GearIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)
const SunIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" /></svg>
)
const MoonIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" /></svg>
)
const UserIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
)
const LogoutIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
)

const iconBtn =
  'relative flex items-center justify-center w-9 h-9 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover transition-colors'

interface TopBarProps {
  isDark: boolean
  onToggleDark: () => void
}

/**
 * Global top header bar. Right-aligned controls: AI Copilot, Sound (mute toggle),
 * Settings (gear, supervisor+), and a Profile dropdown (Go to Profile, Log out,
 * availability + dark mode). These were previously scattered across the sidebar
 * footer and the Chats page header.
 */
export const TopBar: React.FC<TopBarProps> = ({ isDark, onToggleDark }) => {
  const { currentAgent, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { toggleMute, isMuted } = useAudioAlerts()
  const { requestSwitch } = useAppMode()
  const [menuOpen, setMenuOpen] = useState(false)
  const [availability, setAvailability] = useState('offline')
  const menuRef = useRef<HTMLDivElement>(null)

  const roleLabel = (currentAgent as any)?.roleLabel || (currentAgent?.role === 'admin' ? 'Admin' : currentAgent?.role === 'supervisor' ? 'Supervisor' : 'Agent')

  // Persisted availability (same source the old sidebar footer used).
  useEffect(() => {
    if (!currentAgent?.id) return
    let cancelled = false
    apiGet<{ agents: Array<{ id: number | string; availability_status?: string }> }>('/agents/availability')
      .then((res) => {
        if (cancelled) return
        const me = (res.agents || []).find((a) => String(a.id) === String(currentAgent.id))
        if (me?.availability_status) setAvailability(me.availability_status)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [currentAgent?.id])

  // Close the profile menu on outside click.
  useEffect(() => {
    if (!menuOpen) return
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [menuOpen])

  const onSettings = location.pathname.startsWith('/settings')
  const onCopilot = location.pathname.startsWith('/ai-copilot')
  const [glossaryOpen, setGlossaryOpen] = useState(false)

  const handleLogout = () => {
    setMenuOpen(false)
    logout()
    navigate('/login')
  }

  return (
    <header className="flex items-center justify-end gap-1 h-14 px-4 bg-surface border-b border-border shrink-0">
      {/* HELP → Metrics Glossary pop-up (Lee's notebook, 26 Oct 2026). */}
      <button
        onClick={() => setGlossaryOpen(true)}
        className="flex items-center justify-center w-9 h-9 rounded-lg text-txt-secondary border border-border hover:text-txt-primary hover:bg-surface-hover transition-colors"
        title="Metrics Glossary — what every number means"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" /></svg>
      </button>
      {glossaryOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6" style={{ background: 'rgba(15,23,42,.5)' }} onClick={() => setGlossaryOpen(false)}>
          <div className="w-[760px] max-w-full max-h-[85vh] overflow-y-auto rounded-xl bg-surface border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-surface border-b border-border px-5 py-3 flex items-center justify-between">
              <p className="text-[14px] font-bold text-txt-primary">Metrics Glossary</p>
              <button onClick={() => setGlossaryOpen(false)} className="text-txt-tertiary hover:text-txt-primary text-[18px] leading-none">×</button>
            </div>
            <CrmMetricsGlossary />
          </div>
        </div>
      )}
      {/* CRM alerts bell at the absolute top (Lee, 28 Oct 2026) — next to Copilot. */}
      <CrmAlertsBell onOpenDeal={(id) => navigate(`/crm/lead/${id}`)} />
      <button
        onClick={() => navigate('/ai-copilot')}
        className={`relative flex items-center justify-center w-9 h-9 rounded-lg text-white bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 shadow-sm transition-all ${
          onCopilot ? 'ring-2 ring-fuchsia-400/50' : ''
        }`}
        title="AI Copilot"
      >
        <SparklesIcon />
      </button>

      {/* Dev Mode — switches to the blank dev workspace (confirmation first). */}
      <button
        onClick={() => requestSwitch('dev')}
        className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold text-txt-secondary border border-border hover:text-txt-primary hover:bg-surface-hover transition-colors"
        title="Switch to Dev Mode"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
        </svg>
        <span className="hidden sm:inline">Dev Mode</span>
      </button>

      <button
        onClick={toggleMute}
        className={`${iconBtn} ${isMuted ? 'text-error hover:text-error' : ''}`}
        title={isMuted ? 'Unmute audio alerts' : 'Mute audio alerts'}
      >
        {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
      </button>

      {/* Settings gear — visible to EVERYONE (Lee, 29 Jul 2026). The sections inside
          the hub are still role-gated individually. */}
      <button
        onClick={() => navigate('/settings')}
        className={`${iconBtn} ${onSettings ? 'text-brand-600 bg-brand-50 dark:bg-brand-900/20' : ''}`}
        title="Settings"
      >
        <GearIcon />
      </button>

      <div className="w-px h-6 bg-border mx-1" />

      {/* Profile dropdown */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-surface-hover transition-colors"
          title="Account"
        >
          {currentAgent?.avatar_url ? (
            <img src={currentAgent.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-600 text-white text-xs font-semibold">
              {currentAgent?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
          )}
          <span className="hidden sm:block text-left min-w-0">
            <span className="block text-sm font-medium text-txt-primary truncate max-w-[140px]">{currentAgent?.name || 'Agent'}</span>
            <span className="block text-[11px] text-txt-tertiary">{roleLabel}</span>
          </span>
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-full mt-2 w-64 rounded-xl bg-surface border border-border shadow-lg z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-semibold text-txt-primary truncate">{currentAgent?.name || 'Agent'}</p>
              <p className="text-xs text-txt-tertiary truncate">{currentAgent?.email || roleLabel}</p>
            </div>

            <div className="px-3 py-3 border-b border-border">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2 px-1">Availability</p>
              <AgentAvailabilityToggle currentStatus={availability} onStatusChange={setAvailability} />
            </div>

            <div className="py-1.5">
              <button
                onClick={() => { setMenuOpen(false); navigate('/profile') }}
                className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-txt-primary hover:bg-surface-hover transition-colors"
              >
                <UserIcon /> Go to Profile
              </button>
              <button
                onClick={() => { onToggleDark() }}
                className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-txt-primary hover:bg-surface-hover transition-colors"
              >
                {isDark ? <SunIcon /> : <MoonIcon />} {isDark ? 'Light mode' : 'Dark mode'}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-error hover:bg-error/5 transition-colors"
              >
                <LogoutIcon /> Log out
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
